/**
 * ═══════════════════════════════════════════════════════════════
 *  ACTION IDS — Named constants for every gated action
 * ═══════════════════════════════════════════════════════════════
 *
 * SINGLE SOURCE OF TRUTH for action ID strings.
 * Every GatedAction / useAction call MUST reference a constant from here.
 *
 * WHY:
 *   - Eliminates typos and duplicate action IDs
 *   - IDE autocomplete: type ACTION_IDS. and see all options
 *   - Easy for any agent to scan what exists and add new ones
 *   - Rename-safe: change the string value in one place
 *
 * HOW TO ADD A NEW ACTION:
 *   1. Pick the right area section below
 *   2. Add a constant: AREA_FEATURE_VERB: "area.feature.verb"
 *   3. Add a matching entry in permissionMap.js using this constant
 *   4. Use ACTION_IDS.AREA_FEATURE_VERB in your <GatedAction> or useAction()
 *
 * NAMING CONVENTION:
 *   Constant name  →  AREA_FEATURE_VERB  (UPPER_SNAKE_CASE)
 *   String value   →  area.feature.verb  (dot-separated lowercase)
 */

const ACTION_IDS = {
  // ── FINANCE ─────────────────────────────────────────────────
  FINANCE_ACCOUNT_CREATE:        "finance.account.create",
  FINANCE_ACCOUNT_RECORD_ENTRY:  "finance.account.record_entry",
  FINANCE_PAYMENT_APPROVE:       "finance.payment.approve",
  FINANCE_PAYMENT_REJECT:        "finance.payment.reject",
  FINANCE_ACCOUNT_TRANSFER:      "finance.account.transfer",
  FINANCE_ACCOUNT_ACKNOWLEDGE:   "finance.account.acknowledge",
  FINANCE_ACCOUNT_CLOSE:         "finance.account.close",
  FINANCE_ACCOUNT_DELETE:         "finance.account.delete",
  FINANCE_PAY_COMPONENT_EDIT:    "finance.pay_component.edit",
  FINANCE_PAY_COMPONENT_DELETE:  "finance.pay_component.delete",
  FINANCE_RECORD_CHEQUE_DEBIT:   "finance.record_cheque_debit",

  // ── DEVELOPMENT ──────────────────────────────────────────────
  DEVELOPMENT_CREATE:            "development.create",

  // ── VENDORS ─────────────────────────────────────────────────
  VENDORS_VENDOR_ADD:            "vendors.vendor.add",
  VENDORS_VENDOR_EDIT:           "vendors.vendor.edit",
  VENDORS_VENDOR_DEACTIVATE:     "vendors.vendor.deactivate",
  VENDORS_CONTRACT_ADD:          "vendors.contract.add",

  // ── COMMUNITY ───────────────────────────────────────────────
  COMMUNITY_STRUCTURE_ADD:       "community.structure.add",
  COMMUNITY_MEMBER_ASSIGN:       "community.member.assign",
  COMMUNITY_DATA_CLEAR:          "community.data.clear",

  // ── INVITATIONS ─────────────────────────────────────────────
  INVITATIONS_MEETING_CREATE:    "invitations.meeting.create",
  INVITATIONS_MEETING_SAVE:      "invitations.meeting.save",
  INVITATIONS_MEETING_ACTIVATE:  "invitations.meeting.activate",
  INVITATIONS_MEETING_CONCLUDE:  "invitations.meeting.conclude",
  INVITATIONS_MEETING_DELETE:    "invitations.meeting.delete",

  // ── VISITORS ────────────────────────────────────────────────
  VISITORS_SECURITY_CODE_CREATE: "visitors.security_code.create",
  VISITORS_SECURITY_CODE_DELETE: "visitors.security_code.delete",
  VISITORS_ADD:                  "visitors.visitor.add",
  VISITORS_CHECK_IN:             "visitors.visitor.check_in",
  VISITORS_CHECK_OUT:            "visitors.visitor.check_out",
  VISITORS_CANCEL:               "visitors.visitor.cancel",
  VISITORS_DELETE:               "visitors.visitor.delete",

  // ── SOCIAL ──────────────────────────────────────────────────
  SOCIAL_POST_CREATE:            "social.post.create",
  SOCIAL_POST_DELETE_ANY:        "social.post.delete_any",
  SOCIAL_SKILL_CREATE:           "social.skill.create",
  SOCIAL_COMMUNITY_PROFILE_EDIT: "social.community_profile.edit",
  SOCIAL_COMMUNITY_FOLLOW:       "social.community.follow",

  // ── AMENITY BOOKING ─────────────────────────────────────────
  AMENITY_CREATE:                "amenity.create",
  AMENITY_EDIT:                  "amenity.edit",
  AMENITY_DELETE:                "amenity.delete",
  AMENITY_BOOKING_CANCEL:        "amenity.booking.cancel",

  // ── DAILY HELP ──────────────────────────────────────────────
  DAILY_HELP_CREATE:             "daily_help.helper.create",
  DAILY_HELP_DELETE:             "daily_help.helper.delete",

  // ── DOCUMENTS ───────────────────────────────────────────────
  DOCUMENTS_CREATE:              "documents.document.create",
  DOCUMENTS_EDIT:                "documents.document.edit",
  DOCUMENTS_DELETE:              "documents.document.delete",

  // ── EMERGENCY CONTACTS ──────────────────────────────────────
  EMERGENCY_CONTACT_CREATE:      "emergency.contact.create",
  EMERGENCY_CONTACT_EDIT:        "emergency.contact.edit",
  EMERGENCY_CONTACT_DELETE:      "emergency.contact.delete",

  // ── NOTICES ─────────────────────────────────────────────────
  NOTICES_CREATE:                "notices.notice.create",
  NOTICES_PIN:                   "notices.notice.pin",
  NOTICES_DELETE:                "notices.notice.delete",

  // ── POLLS ───────────────────────────────────────────────────
  POLLS_CREATE:                  "polls.poll.create",
  POLLS_CLOSE:                   "polls.poll.close",
  POLLS_DELETE:                  "polls.poll.delete",

  // ── ENGAGEMENT — STORY WALL ─────────────────────────────────
  ENGAGEMENT_STORY_CREATE:       "engagement.story.create",
  ENGAGEMENT_STORY_DELETE:       "engagement.story.delete",
  ENGAGEMENT_STORY_REACT:        "engagement.story.react",

  // ── ENGAGEMENT — OFFERINGS ──────────────────────────────────
  ENGAGEMENT_OFFERING_CREATE:    "engagement.offering.create",
  ENGAGEMENT_OFFERING_CLAIM:     "engagement.offering.claim",
  ENGAGEMENT_OFFERING_FULFILL:   "engagement.offering.fulfill",
  ENGAGEMENT_OFFERING_DELETE:    "engagement.offering.delete",

  // ── ENGAGEMENT — BUDDY GROUPS ───────────────────────────────
  ENGAGEMENT_BUDDY_CREATE:       "engagement.buddy.create",
  ENGAGEMENT_BUDDY_JOIN:         "engagement.buddy.join",
  ENGAGEMENT_BUDDY_POST:         "engagement.buddy.post",

  // ── ENGAGEMENT — PUZZLES & GAMES ────────────────────────────
  ENGAGEMENT_PUZZLE_PLAY:        "engagement.puzzle.play",
  ENGAGEMENT_GAME_CREATE:        "engagement.game.create",
  ENGAGEMENT_GAME_PLAY:          "engagement.game.play",

  // ── ENGAGEMENT — CONTRIBUTIONS ───────────────────────────────
  ENGAGEMENT_CONTRIBUTE:         "engagement.contribute",

  // ── BILLING — VARIABLE INPUTS ───────────────────────────────
  FINANCE_BILLING_INPUT_MANAGE:  "finance.billing_input.manage",
  FINANCE_BILL_HOLD:             "finance.bill.hold",
};

// Dev-time duplicate-value check
if (process.env.NODE_ENV === "development") {
  const values = Object.values(ACTION_IDS);
  const dupes = values.filter((v, i) => values.indexOf(v) !== i);
  if (dupes.length) {
    console.error(
      `[actionIds] DUPLICATE action ID values detected: ${dupes.join(", ")}. ` +
      `Each action must have a unique string value.`
    );
  }
}

Object.freeze(ACTION_IDS);

export default ACTION_IDS;

/**
 * Button access types — determines who can use the action.
 *
 *  PUBLIC:     Anyone in the community (default for most actions)
 *  RESTRICTED: Only committee members with the relevant area
 *  HIDDEN:     Not visible to unauthorized users at all
 */
export const ACTION_TYPES = Object.freeze({
  PUBLIC: "public",
  RESTRICTED: "restricted",
  HIDDEN: "hidden",
});
